import React, { useEffect } from 'react';

import {
    PRESCRIPEUR_TACHES_SLICE_NAME,
    prescripeurTachesSelector,
    useTiersService,
    useUtilisateurService,
} from '@europrocurement/l2d-domain';
import {
    EmptyBox,
    HeaderListSkeleton,
    ListHeader,
    ListSkeleton,
    SkeletonLoader,
} from '@europrocurement/flexy-components';
import { Box } from '@mui/material';
import { StoreDatatable } from '@europrocurement/flexy-datatable';
import { useInitialLoadingState } from '@europrocurement/l2d-hooks';
import { useDispatch } from 'react-redux';
import { useTasksTableHook } from '@europrocurement/l2d-modules';
import { UseKeycloakService } from '@europrocurement/l2d-keycloak';
import { useSnackbar } from 'notistack';
import { customizerSelector } from '@europrocurement/flexy-components/redux/storeConfig/selectors';
import useTasksHook from '@europrocurement/l2d-modules/modules/Tasks/hook/useTasksHook';
import { PrescripteurTachesApiObject } from '@europrocurement/l2d-domain/reducers/tiers/slices/prescripteurTachesSlice';
import { type AppDispatch, useAppSelector } from '../../../../redux/types';

/**
 * Page de listing des taches liées aux dossiers de formalités.
 */
const ListeTachesPrescripeurs: React.FunctionComponent = function () {
    const dispatch = useDispatch<AppDispatch>();
    const datasource = useAppSelector(prescripeurTachesSelector).main;
    const { getPrescripteursTaches } = useTiersService();
    const { isInitialLoading, isInError } = useInitialLoadingState(datasource);
    const { getColumnsTachesPrescripteurs, getFiltersTachesPrescripteurs } = useTasksTableHook({
        apiTarget: 'tiers',
    });
    const { handleEditTask } = useTasksHook({ apiTarget: 'tiers' });
    const { utilisateurApi } = useUtilisateurService();
    const keycloakService = UseKeycloakService();
    const userIdentifier = keycloakService.getTokenParsed()?.preferred_username;
    const isFormalistManager = keycloakService.checkRole('realm:responsable formaliste');
    const { enqueueSnackbar } = useSnackbar();
    const { xIdSociete } = useAppSelector(customizerSelector);

    const onClickRow = (e: React.MouseEvent, task: PrescripteurTachesApiObject) => {
        handleEditTask(task);
    };

    // Chargement initial des données
    // Filtre les tâches de l'utilisateur connecté ou affiche l'ensemble des tâches pour un manager
    useEffect(() => {
        if (isFormalistManager) {
            dispatch({
                type: `${PRESCRIPEUR_TACHES_SLICE_NAME}/setmainFilter`,
                payload: { key: 'xIdSociete', value: [xIdSociete] },
            });
            dispatch(getPrescripteursTaches({}));
        } else if (userIdentifier) {
            dispatch({
                type: `${PRESCRIPEUR_TACHES_SLICE_NAME}/setmainFilter`,
                payload: { key: 'xIdSociete', value: [xIdSociete] },
            });
            utilisateurApi.apiUtilisateursGetCollection({ userIdentifier }).then((response) => {
                const user = response.data['hydra:member'][0] ?? null;

                if (user && user.userIdentifier === userIdentifier) {
                    dispatch({
                        type: `${PRESCRIPEUR_TACHES_SLICE_NAME}/setmainFilter`,
                        payload: {
                            key: 'tacheAssignTo',
                            value: `tiers/user/${user.id}`,
                        },
                    });
                    dispatch(getPrescripteursTaches({}));
                } else {
                    enqueueSnackbar('Utilisateur non trouvé - identifiant non conforme', {
                        variant: 'error',
                    });
                }
            });
        } else {
            enqueueSnackbar('Utilisateur non trouvé', { variant: 'error' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userIdentifier, xIdSociete]);

    const errorContent = React.useMemo(
        () => (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '80vh',
                    width: '100%',
                }}
            >
                <EmptyBox
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'space-evenly',
                        height: '25%',
                        width: '50%',
                    }}
                    text="Une erreur s'est produite lors de la récupération des données, échec du chargement des tâches."
                    subText="Veuillez recharger la page. Si le problème persiste, contactez votre administrateur."
                />
            </Box>
        ),
        [],
    );

    return (
        <SkeletonLoader
            isLoading={isInitialLoading}
            skeleton={
                <>
                    <HeaderListSkeleton />
                    <ListSkeleton
                        showSearch
                        columns={6}
                    />
                </>
            }
            isInError={isInError}
            errorChildren={errorContent}
        >
            <ListHeader breadcrumbPath={['Liste des tâches liées aux prescripteurs']} />
            {datasource && (
                <StoreDatatable
                    dataSource={datasource}
                    columns={getColumnsTachesPrescripteurs()}
                    fetchData={getPrescripteursTaches}
                    filters={getFiltersTachesPrescripteurs()}
                    localStorageKey="FlexyStoreDatatable"
                    onClickRow={onClickRow}
                    observers={[xIdSociete]}
                />
            )}
        </SkeletonLoader>
    );
};

export default ListeTachesPrescripeurs;
